import { type AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'

import { SettingsController } from '@/app/api'
import { type IApiErrorHandler } from '@/app/types/common'
import { type IUserSlot, type IBugReport } from '@/app/types/settingsTypes'
import { onHandleResponseNotification, onUseQueries } from '@/app/utils'

class Settings {
    bugReportList: IBugReport[] | null = null
    searchableAgentId: string | null = null
    userSlotsList: IUserSlot[] | null = null

    constructor() {
        makeAutoObservable(this)
    }

    onSetUserSlotsList = (userSlotsList: IUserSlot[] | null) => {
        this.userSlotsList = userSlotsList
    }

    onSetSearchableAgentId = (searchableAgentId: string | null) => {
        this.searchableAgentId = searchableAgentId
    }

    onSetBugReportList(bugReportList: IBugReport[] | null) {
        this.bugReportList = bugReportList
    }

    async onGetBugReportList() {
        const client = new SettingsController()
        try {
            const { data } = await client.onGetBugReportList()
            this.onSetBugReportList(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
            this.onSetBugReportList([])
        }
    }

    async onRemoveBugReport(id: string) {
        const client = new SettingsController()
        try {
            const { status } = await client.onRemoveBugReport(id)
            if (status === 204) {
                this.onSetBugReportList(null)
                this.onGetBugReportList()
                onHandleResponseNotification(status, 'Сообщение удалено')
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    async onUploadAppLogo(file: Blob) {
        const client = new SettingsController()
        const dto = new FormData()
        dto.append('file', file)

        try {
            const { data } = await client.onUploadAppLogo(dto)
            if (data) {
                window.location.reload()
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    onGetSlotsByAgentId = async (agentId: string) => {
        const client = new SettingsController()
        try {
            const { data } = await client.onGetSlotsByAgentId(agentId)
            this.onSetUserSlotsList(data)
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }

    onRefundRecord = async (slotId: string, agentId: string, onSuccess?: () => void) => {
        const client = new SettingsController()
        const queries = onUseQueries({ slotId, agentId })
        try {
            const { status } = await client.onRefundRecord(queries)
            if (status >= 100 && status < 300 && onSuccess) {
                onSuccess()
                onHandleResponseNotification(status, 'Запись успешно отменена')
            }
        } catch (e) {
            const error = e as AxiosError<IApiErrorHandler>
            onHandleResponseNotification(error.response?.status || 500, error.response?.data?.detail)
        }
    }
}

export default new Settings()
